import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Input, Card } from '@/components';
import { supabase } from '@/lib/supabase';

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [errors, setErrors] = useState({
    password: '',
    passwordConfirmation: '',
    submit: '',
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidSession, setIsValidSession] = useState(false);

  // Verify that a valid recovery session exists on mount
  useEffect(() => {
    document.title = 'Set a New Secure Password | PrintDeck Direct';
    const checkSession = async () => {
      try {
        // Wait briefly for Supabase to process URL hash fragments if they exist
        await new Promise((resolve) => setTimeout(resolve, 500));
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setIsValidSession(true);
        } else {
          setErrors((prev) => ({
            ...prev,
            submit: 'Your password reset link is invalid or has expired. Please request a new one.',
          }));
        }
      } catch (err) {
        console.error('[ResetPassword] Session check error:', err);
        setErrors((prev) => ({
          ...prev,
          submit: 'Failed to verify session. Please try requesting a new link.',
        }));
      } finally {
        setIsVerifying(false);
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({ password: '', passwordConfirmation: '', submit: '' });

    // Validate passwords
    if (!password) {
      setErrors((prev) => ({ ...prev, password: 'Password is required' }));
      return;
    }

    if (password.length < 8) {
      setErrors((prev) => ({ ...prev, password: 'Password must be at least 8 characters' }));
      return;
    }

    if (!passwordConfirmation) {
      setErrors((prev) => ({ ...prev, passwordConfirmation: 'Please confirm your password' }));
      return;
    }

    if (password !== passwordConfirmation) {
      setErrors((prev) => ({ ...prev, passwordConfirmation: 'Passwords do not match' }));
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      // Log out to clear the active recovery session
      await supabase.auth.signOut();

      // Redirect to login with success message
      navigate('/login', {
        state: { message: 'Password reset successful! Please sign in with your new password.' },
      });
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to reset password. Please try again.';
      setErrors((prev) => ({ ...prev, submit: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">
              Reset Password
            </h1>
            <p className="text-gray-600 mt-2 text-sm">
              {isVerifying 
                ? 'Verifying your recovery session...' 
                : isValidSession 
                  ? 'Create a new secure password for your account' 
                  : 'Invalid reset session'}
            </p>
          </div>

          {/* Loader */}
          {isVerifying && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          )}

          {/* Error Banner */}
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in" role="alert">
              <p className="text-red-800 text-xs">✕ {errors.submit}</p>
            </div>
          )}

          {/* Password Form (Only if session is verified) */}
          {!isVerifying && isValidSession && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <Input
                id="password"
                label="New Password"
                type="password"
                name="password"
                placeholder="Enter a strong password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                fullWidth
                disabled={isLoading}
              />

              <Input
                id="passwordConfirmation"
                label="Confirm Password"
                type="password"
                name="passwordConfirmation"
                placeholder="Confirm your password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                error={errors.passwordConfirmation}
                fullWidth
                disabled={isLoading}
              />

              <Button
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                isLoading={isLoading}
                disabled={isLoading}
              >
                Reset Password
              </Button>
            </form>
          )}

          {/* Footer link to request a new one if session is invalid */}
          {!isVerifying && !isValidSession && (
            <div className="text-center pt-2">
              <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700 font-semibold text-xs">
                Request a new reset link
              </Link>
            </div>
          )}

          {/* General Back to Sign In Link */}
          {isValidSession && (
            <div className="text-center pt-2">
              <p className="text-gray-600 text-xs">
                Remember your password?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold">
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
